import { Vue, Component, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import {
  TicketDetailDTO,
  TicketMessageAnswerDTO,
  TicketMessageDTO,
} from '@/../client-generator/generated-clients/tickets';
import { UserProfileDTO } from '@/../client-generator/generated-clients/user';

import ticketClient from '@/core/api/tickets-service';
import documentsClient from '@/core/api/documents-service';
import { LOADING_CONTENT } from '@/core/store/types/action-types';

import { ticketUtils } from '@/core/utilities/ticket-utilities';
import fileDownload from 'js-file-download';

import SVGLogoPulinetSmall from '@/assets/img/logo/logo-pulinet-small.svg';
import { Route } from 'vue-router';

import AppTicketResolvedModal from '@/views/components/AppTicketResolvedModal/AppTicketResolvedModal.vue';

@Component({
  components: {
    SVGLogoPulinetSmall,
    AppTicketResolvedModal,
  },
  computed: {
    ...mapState(['profile']),
  },
  i18n: {
    messages: {
      it: {
        ticketNotFound: 'Ticket non trovato',
        fileNotFound: 'Documento non trovato',
        fileNotAssociatedWithUser: "File non più associato all'utente",
        emptyMessage: 'Il messaggio è vuoto',
      },
    },
  },
})
export default class TicketDetail extends Vue {
  isLoading = false;
  ticketUtils = ticketUtils;

  resolvedDialogVisible = false;

  ticket: TicketDetailDTO | null = null;
  ticketId: number | null = null;
  forceAnswerTextAreaVisible = false;

  get answerTextAreaVisible() {
    return (
      (ticketUtils.isTicketAnswerConsulted(this.ticket) || this.forceAnswerTextAreaVisible) &&
      this.ticket?.hasPermissionToManage
    );
  }

  formModel: {
    answerText: string;
    messageEditedRefId: number | null;
  } = {
    answerText: '',
    messageEditedRefId: null,
  };

  profile!: UserProfileDTO;

  get userName() {
    if (this.profile) {
      return this.profile.username;
    }

    return '';
  }

  get firstLetterUsername() {
    if (this.userName?.length) return this.userName.substr(0, 1).toUpperCase();
    return 'A';
  }

  get userAvatarURL() {
    return 'https://via.placeholder.com/40x40';
  }

  @Watch('$route', { deep: true })
  routeUpdated(route: Route) {
    this.ticketId = parseInt(route.params.ticketId, 10);
    this.fetchTicket();
  }

  mounted() {
    if (this.$route.params.ticketId) this.ticketId = parseInt(this.$route.params.ticketId, 10);
    this.fetchTicket();
  }

  async fetchTicket() {
    if (!this.ticketId) return;
    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      this.ticket = await ticketClient.details(this.ticketId);
      if (this.ticket?.messages) this.ticket.messages.reverse();
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('ticketNotFound').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async downloadDocument(documentId, fileName: string) {
    if (!(documentId && fileName && this.ticketId)) return;
    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      const response = await documentsClient.download(documentId, fileName, this.ticketId.toString());
      fileDownload(response.data, fileName);
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('fileNotFound').toString();
          break;
        case 406:
          messageError = this.$t('fileNotAssociatedWithUser').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async answerToTicket() {
    try {
      if (!this.ticketId) return;
      this.isLoading = true;
      const request = {
        message: this.formModel.answerText,
        messageEditedRefId: this.formModel.messageEditedRefId,
      };

      await ticketClient.answer(this.ticketId, request as TicketMessageAnswerDTO);
      this.fetchTicket();
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 400:
          messageError = this.$t('emptyMessage').toString();
          break;
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('ticketNotFound').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.cancelAnswer();
      this.isLoading = false;
    }
  }

  cancelAnswer() {
    this.formModel.answerText = '';
    this.formModel.messageEditedRefId = null;
    this.forceAnswerTextAreaVisible = false;
  }

  backToList() {
    this.$router.push({
      name: 'page-tickets',
    });
  }

  isMe(messageUsername: string) {
    return this.userName === messageUsername;
  }

  toLocaleString(date) {
    return new Date(date).toLocaleString();
  }

  editAnswer(message: TicketMessageDTO) {
    this.formModel = {
      answerText: message.message!,
      messageEditedRefId: message.messageId!,
    };
    this.forceAnswerTextAreaVisible = true;
  }
}
